import Ad from '@/entities/Ad';
import BaseEntity from '@/entities/BaseEntity';
import Campaign from '@/entities/Campaign';
import LeadgenFormLeadActivity from '@/entities/LeadgenFormLeadActivity';
import User from '@/entities/User';

// The words of the name questions in several languages (the field names are the questions' keys,
// in the language of the form), and of the full name ones, which are preferred.
const NAME_WORDS = [ 'name', 'fullname', 'firstname', 'lastname', 'nume', 'prenume', 'név', 'nev', 'vezetéknév', 'keresztnév', 'nom', 'prénom', 'nombre', 'vorname', 'nachname', 'imię', 'nazwisko', 'jméno', 'meno' ];
const FULL_NAME_WORDS = [ 'full', 'fullname', 'complet', 'teljes', 'completo', 'vollständiger' ];

function fieldWords(fieldName) {
  return fieldName.toLowerCase().split(/[\s_\-.]+/);
}

/** The name questions of a form: its full name question, or else all its name parts. */
export function findNameFields(fieldNames) {
  const nameFields = fieldNames.filter(fieldName => fieldWords(fieldName).some(word => NAME_WORDS.includes(word)));
  const fullNameField = nameFields.find(fieldName => fieldWords(fieldName).some(word => FULL_NAME_WORDS.includes(word)));

  return fullNameField ? [ fullNameField ] : nameFields;
}

export default class LeadgenFormLead extends BaseEntity {
  id = null;
  external_id = null;
  leadgen_form_id = null;
  created_at = null;
  ad = null;
  campaign = null;
  rule = null;
  field_data = [];
  status = 'new';
  assignee = null;
  leadgen_form = null;
  activities = [];

  _entityFields = {
    ad: Ad,
    campaign: Campaign,
    assignee: User,
  };

  _entityArrayFields = {
    activities: LeadgenFormLeadActivity,
  };

  constructor(rawData) {
    super();
    this.updateWith(rawData);
  }

  /** The values of an answered field with their mailto / tel links. */
  getFieldItems(fieldName) {
    const field = (this.field_data ?? []).find(item => item.name === fieldName);
    if (!field || !Array.isArray(field.values)) {
      return [];
    }

    return field.values.map((value, index) => ({
      value,
      link: field.links?.[index] ?? null,
    }));
  }

  getFieldValue(fieldName) {
    return this.getFieldItems(fieldName).map(item => item.value).join(', ');
  }

  get displayName() {
    const fieldNames = (this.field_data ?? []).map(field => field.name ?? '');
    const value = findNameFields(fieldNames)
      .map(fieldName => this.getFieldValue(fieldName))
      .filter(Boolean)
      .join(' ');

    return value || this.email || this.phone || '';
  }

  // The contacts are recognised by the links the backend adds to them.
  get phone() {
    return this.findLinkedValue('tel:');
  }

  get email() {
    return this.findLinkedValue('mailto:');
  }

  findLinkedValue(prefix) {
    for (const field of this.field_data ?? []) {
      const index = (field.links ?? []).findIndex(link => link?.startsWith(prefix));
      if (index !== -1) {
        return field.values?.[index] ?? null;
      }
    }

    return null;
  }
}
